import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Lightbulb, Rocket, Shield, Target, GitBranch, Users2,
  Eye, TrendingUp, Database, BarChart3, Brain, Wand2,
  Users, Scale, FileCheck, Map,
  TreePine, Layers, Share2, Gauge, Copy, RefreshCw,
  Activity, LayoutTemplate, ShieldCheck, Clock
} from 'lucide-react';
import Header from '../layout/Header';
import ExpertVisionModal from '../components/ExpertVisionModal';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

// Interface pour les concepts
interface Concept {
  id: number;
  title: string;
  icon: React.ComponentType<{ className?: string; size?: string | number }>;
  description: string;
  principles: string[];
  color: string;
  inKnowledgeBase?: boolean; // 🧠 Indique si le contenu est dans la knowledge base AI Assistant
  reference?: string; // Référence ou source officielle (cadre, norme, standard, auteur)
  scoreDany?: number;
  scoreYannick?: number;
  scoreFrancois?: number;
  visionDany?: string;
  visionYannick?: string;
  visionFrancois?: string;
  category?: string;
}

// Standards BZ Inc. d'affaires
const standardsBZ: Concept[] = [
  {
    id: 1,
    title: "Panorama (Overview)",
    icon: Eye,
    description: "Documentation interactive 360° présentant l'architecture, la cybersécurité, les KPI, le ROI, la gouvernance et la vision stratégique d'un système.",
    principles: [
      "Vue d'ensemble complète en 10 sections détaillées",
      "Architecture technique documentée et visualisée",
      "Scores NIST et évaluation cybersécurité transparente"
    ],
    color: "from-purple-500 to-pink-500",
    inKnowledgeBase: true, // 🧠 Contenu dans AI Assistant
    reference: "Standard interne BZ inc. inspiré des cadres NIST CSF 2.0, ITIL 4, COBIT 2019 et ISO/IEC 27001",
    scoreDany: 5,
    scoreYannick: 5,
    scoreFrancois: 5,
    category: "Standard BZ",
    visionDany: "Le Panorama est notre signature. C'est la vitrine complète qui montre au client exactement où il se trouve et où il va. Transparence totale sur l'architecture, la sécurité, les coûts et le ROI.",
    visionYannick: "Du point de vue commercial, le Panorama est notre meilleur outil de vente. Il démontre instantanément notre niveau de professionnalisme et notre approche structurée.",
    visionFrancois: "Techniquement, c'est la documentation la plus complète qu'un client puisse avoir. Tous les systèmes, toutes les intégrations, tous les risques - tout est là, visualisé et accessible."
  },
  {
    id: 2,
    title: "ASWAS → ASIS → Cible (Target) → Écart (GAP)",
    icon: TrendingUp,
    description: "Méthodologie de progression mesurable: état passé de référence (ASWAS), état actuel (ASIS), état cible (ToBe / Target) et écart (GAP) à combler.",
    principles: [
      "Mesure objective de la maturité TI sur une échelle 0-100 (ASIS → Cible)",
      "Analyse structurée ASWAS / ASIS / ToBe (Target) pour visualiser les écarts (GAP)",
      "Priorisation des actions basée sur les écarts les plus critiques pour l'organisation"
    ],
    color: "from-blue-500 to-green-500",
    inKnowledgeBase: true, // 🧠 Contenu dans AI Assistant
    reference: "Analyse AS-IS / TO-BE et gestion d'écarts (Gap Analysis) utilisées en amélioration continue (BABOK, BPM, Lean Six Sigma), adaptées par BZ inc. avec la notion d'ASWAS",
  },
  {
    id: 3,
    title: "Cadres Référentiels",
    icon: Database,
    description: "Maîtrise et application de standards reconnus: Cadre BZ, NIST Cybersécurité, NIST IA, ITIL, ISO 27001 pour structurer l'excellence.",
    principles: [
      "Cadre BZ: 7 piliers, 70+ catégories de maturité TI",
      "NIST Cybersecurity Framework pour la sécurité",
      "NIST AI Framework pour l'éthique et la gouvernance IA"
    ],
    color: "from-green-500 to-green-600",
    inKnowledgeBase: true, // 🧠 Contenu dans AI Assistant
    reference: "Cadre BZ + NIST CSF 2.0 + NIST AI RMF + ITIL 4 + ISO/IEC 27001 + COBIT 2019",
  },
  {
    id: 4,
    title: "KPI & Indicateurs de Performance",
    icon: BarChart3,
    description: "Définition et suivi d'indicateurs concrets et mesurables en collaboration avec le client pour démontrer la valeur ajoutée.",
    principles: [
      "KPI partagés et co-construits avec le client",
      "Dashboards temps réel accessibles et transparents",
      "ROI démontrable à chaque trimestre"
    ],
    color: "from-teal-500 to-green-400",
    inKnowledgeBase: true, // 🧠 Contenu dans AI Assistant
    reference: "PMBOK 7e édition, Balanced Scorecard (Kaplan & Norton)",
  },
  {
    id: 5,
    title: "IA Éthique & Responsable",
    icon: Brain,
    description: "Approche éthique de l'intelligence artificielle: transparence totale, contrôle humain et protection des données sensibles.",
    principles: [
      "100% des décisions restent humaines, l'IA propose",
      "Aucune donnée sensible transmise aux modèles externes",
      "Traçabilité complète de toutes les interactions IA"
    ],
    color: "from-purple-600 to-purple-700",
    inKnowledgeBase: true, // 🧠 Contenu dans AI Assistant
    reference: "NIST AI Risk Management Framework, Loi 25 (Québec), RGPD / GDPR",
  },
  {
    id: 6,
    title: "Magie de l'IA BZ (Triptyque)",
    icon: Wand2,
    description: "L'excellence IA repose sur trois piliers: Hygiène du Prompt, Rigueur Documentaire et Volonté d'Apprendre qui créent la 'magie'.",
    principles: [
      "Hygiène du Prompt: Structure, contexte et clarté des demandes",
      "Rigueur: Documentation exhaustive, code commenté, standards",
      "Volonté: Curiosité, expérimentation et amélioration continue"
    ],
    color: "from-pink-500 to-rose-500",
    inKnowledgeBase: true, // 🧠 Contenu dans AI Assistant
    reference: "Standard interne BZ inc. pour l'utilisation de l'IA",
  }
];

// Concepts BZ Inc. d'affaires
const conceptsBZ: Concept[] = [
  {
    id: 7,
    title: "Profil humain",
    icon: Users,
    description: "Évaluation psychométrique pour comprendre les forces de chacun et créer une complicité authentique employé-client.",
    principles: [
      "8 catégories de compétences évaluées objectivement",
      "Visualisation spider chart pour comprendre les profils",
      "Synergie d'équipe optimisée par complémentarité"
    ],
    color: "from-red-500 to-red-600",
    reference: "Adaptation BZ des approches psychométriques (DISC, Insights, etc.)",
  },
  {
    id: 8,
    title: "Gouvernance RACI",
    icon: Scale,
    description: "Clarté des rôles et responsabilités: Responsible, Accountable, Consulted, Informed. Transparence et autonomie décisionnelle.",
    principles: [
      "Matrice RACI claire pour chaque projet/processus",
      "Élimination des zones grises et des doublons",
      "Autonomie client maximisée avec support expert BZ"
    ],
    color: "from-orange-500 to-orange-600",
    reference: "RACI Matrix – Project Management Institute (PMI), bonnes pratiques de gouvernance",
  },
  {
    id: 9,
    title: "Spécifications d'exigences d'affaires et TI",
    icon: FileCheck,
    description: "Spécifications techniques détaillées, exigences fonctionnelles et critères de qualité documentés pour assurer l'excellence.",
    principles: [
      "Restrictions de sécurité critiques bien définies",
      "Exigences techniques mesurables (performance, SLA)",
      "Bonnes pratiques d'usage documentées"
    ],
    color: "from-green-600 to-green-700",
    reference: "BABOK (IIBA), PMBOK, ISO/IEC 25010, IEEE 830 (Software Requirements Specification)",
  },
  {
    id: 10,
    title: "Roadmap Annuel",
    icon: Map,
    description: "Planification stratégique 12 mois avec phases, jalons et livrables. Applicable en TI comme dans tous les domaines.",
    principles: [
      "Vision claire trimestrielle avec objectifs mesurables",
      "Phases séquencées: Optimisation → IA → Intégration → Innovation",
      "Flexibilité et ajustements selon résultats trimestriels"
    ],
    color: "from-blue-600 to-blue-700",
    reference: "Pratiques de roadmapping stratégique (PMI, gestion de portefeuille, méthodes agiles)",
  }
];

// Concepts généraux existants
const conceptsGeneraux: Concept[] = [
  {
    id: 11,
    title: "Innovation Continue",
    icon: Rocket,
    description: "Créer une culture d'expérimentation et d'amélioration constante dans tous les aspects de l'opération TI.",
    principles: [
      "POC trimestriels pour tester de nouvelles technologies",
      "Budget dédié à l'innovation (10% du temps d'équipe)",
      "Célébration des échecs constructifs"
    ],
    color: "from-blue-500 to-cyan-500",
    reference: "ITIL 4 (Amélioration continue), Lean, Kaizen",
  },
  {
    id: 12,
    title: "Sécurité par Design",
    icon: Shield,
    description: "Intégrer la cybersécurité dès la conception, pas comme une réflexion après coup.",
    principles: [
      "Security reviews à chaque étape du projet",
      "Formation continue sur les menaces émergentes",
      "Architecture Zero Trust comme standard"
    ],
    color: "from-red-500 to-pink-500",
    reference: "Security by Design, NIST SP 800-160, OWASP, Zero Trust Architecture",
  },
  {
    id: 13,
    title: "Alignement Business-TI",
    icon: Target,
    description: "Traduire systématiquement les besoins d'affaires en solutions TI mesurables et rentables.",
    principles: [
      "KPIs partagés entre Business et TI",
      "Réunions stratégiques mensuelles",
      "Tableau de bord ROI en temps réel"
    ],
    color: "from-green-500 to-emerald-500",
    reference: "COBIT 2019 (Alignement Business-TI), gouvernance TI",
  },
  {
    id: 14,
    title: "Agilité Organisationnelle",
    icon: GitBranch,
    description: "Adapter rapidement l'organisation aux changements du marché et aux nouvelles opportunités.",
    principles: [
      "Équipes cross-fonctionnelles autonomes",
      "Processus décisionnels décentralisés",
      "Rétrospectives et pivots réguliers"
    ],
    color: "from-purple-500 to-violet-500",
    reference: "Manifeste Agile, Scrum Guide, pratiques d'agilité organisationnelle",
  },
  {
    id: 15,
    title: "Leadership Collaboratif",
    icon: Users2,
    description: "Promouvoir un style de leadership qui inspire, guide et responsabilise chaque membre de l'équipe.",
    principles: [
      "Coaching individuel mensuel",
      "Décisions collégiales sur les orientations majeures",
      "Transparence totale sur les objectifs et résultats"
    ],
    color: "from-orange-500 to-amber-500",
    reference: "Littérature en leadership collaboratif et management moderne (Lencioni, etc.)",
  },
  {
    id: 16,
    title: "Excellence Opérationnelle",
    icon: Lightbulb,
    description: "Viser l'excellence dans chaque livrable, processus et interaction avec le client.",
    principles: [
      "Standards de qualité documentés et mesurés",
      "Automatisation maximale des tâches répétitives",
      "Culture de la documentation et du partage"
    ],
    color: "from-yellow-500 to-orange-500",
    reference: "Lean Management, Six Sigma, ITIL 4 (Excellence opérationnelle)",
  }
];

// Concepts fondamentaux PRIsME (issus de la modal Overview)
const prismeCoreConcepts: Concept[] = [
  {
    id: 101,
    title: "Arbre PRIsME Tronc–Branches–Feuilles",
    icon: TreePine,
    description: "Représentation systémique où le tronc est la vision, les branches les domaines et les feuilles les livrables et actions concrètes.",
    principles: [
      "Toujours partir du tronc (vision / objectifs) avant de détailler les branches et feuilles",
      "Éviter de travailler feuille par feuille sans voir l'arbre complet",
      "Permet de visualiser rapidement où se situe chaque initiative dans le tout"
    ],
    color: "from-emerald-500 to-green-600",
    reference: "Thinking in Systems – Donella Meadows (approche systémique adaptée par BZ inc.)",
  },
  {
    id: 102,
    title: "Cascade Stratégique–Tactique–Opérationnelle",
    icon: Layers,
    description: "Alignement clair entre la vision stratégique, les objectifs mesurables et les actions tactiques quotidiennes.",
    principles: [
      "Décliner chaque vision stratégique en objectifs mesurables (OKR/Hoshin)",
      "Relier chaque action terrain à un objectif supérieur (pas d'action orpheline)",
      "Utiliser PRIsME + TactiCE pour rendre la cascade visible et vivante"
    ],
    color: "from-indigo-500 to-blue-600",
    reference: "Hoshin Kanri, OKR (Doerr), gestion de portefeuille PMI – adaptés dans TactiCE / PRIsME",
  },
  {
    id: 103,
    title: "RACI & Gouvernance Claire",
    icon: Share2,
    description: "Clarté des rôles (Responsible, Accountable, Consulted, Informed) pour chaque prisme, livrable et décision.",
    principles: [
      "Toujours identifier un imputable (Accountable) par livrable ou prisme",
      "Limiter le nombre de responsables pour éviter la dilution",
      "Documenter la gouvernance PRIsME employés & clients avec des matrices RACI simples"
    ],
    color: "from-orange-500 to-amber-500",
    reference: "RACI / RASCI – PMBOK, ITIL 4, bonnes pratiques de gouvernance",
  },
  {
    id: 104,
    title: "Niveaux de Qualité & Maturité 1→5",
    icon: Gauge,
    description: "Échelle commune 1 à 5 pour exprimer la qualité, la complétude et la maturité des documents, processus et prismes.",
    principles: [
      "Niveau 3 = acceptable pour production, niveau 5 = excellence PRIsME",
      "Utiliser la même échelle dans tous les départements (TI, Ventes, Admin/RH)",
      "Toujours rendre visible le niveau actuel vs le niveau cible"
    ],
    color: "from-yellow-500 to-orange-500",
    reference: "Échelles de maturité (CMMI, COBIT, NIST CSF Tiers) adaptées au modèle PRIsME",
  },
  {
    id: 105,
    title: "PRIsME Master → PRIsME Dérivés",
    icon: Copy,
    description: "Un Master PRIsME BZ qui alimente les prismes employés et clients, avec synchronisation contrôlée.",
    principles: [
      "Tout part du Master (standards, concepts, gabarits) puis se décline",
      "Les prismes employés et clients restent synchronisés tout en étant personnalisés",
      "Les retours terrain nourrissent le Master (boucle de rétroaction)"
    ],
    color: "from-purple-500 to-pink-500",
    reference: "Concept de Digital Twin / jumeau numérique d'organisation, adapté au contexte BZ inc.",
  },
  {
    id: 106,
    title: "Boucles d’Apprentissage Rapides",
    icon: RefreshCw,
    description: "Construire – mesurer – apprendre en cycles courts pour ajuster rapidement standards, gabarits et pratiques.",
    principles: [
      "Préférer des itérations rapides à des déploiements monolithiques",
      "Mesurer systématiquement l’impact des changements PRIsME",
      "Intégrer les boucles d’amélioration directement dans les prismes"
    ],
    color: "from-cyan-500 to-blue-500",
    reference: "Cycle PDCA (Deming), Lean Startup (Build-Measure-Learn), Kaizen",
  },
  {
    id: 107,
    title: "Flow Visuel & Décisionnel",
    icon: BarChart3,
    description: "Transformer les données brutes en visuels clairs pour accélérer la compréhension et la prise de décision.",
    principles: [
      "Toujours viser une visualisation simple pour chaque indicateur clé",
      "Utiliser graphiques, matrices et arbres PRIsME au lieu de tableaux bruts",
      "Relier chaque visuel à une décision ou une action attendue"
    ],
    color: "from-sky-500 to-indigo-500",
    reference: "Data Visualization (Tufte) et décision basée sur les données (Data-Driven Decision Making)",
  },
  {
    id: 108,
    title: "Modularité & Réutilisation",
    icon: LayoutTemplate,
    description: "Travailler avec des gabarits, briques réutilisables et composants pour accélérer et sécuriser les livrables.",
    principles: [
      "Créer des gabarits PRIsME modulaires pour documents et formations",
      "Réutiliser plutôt que repartir de zéro à chaque mandat",
      "Standardiser les blocs critiques (sécurité, loi 25, IA, etc.)"
    ],
    color: "from-fuchsia-500 to-purple-600",
    reference: "Design Systems, architecture orientée composants, bonnes pratiques de réutilisation logicielle",
  },
  {
    id: 109,
    title: "Sécurité & Conformité by Design",
    icon: ShieldCheck,
    description: "Intégrer la sécurité, la protection des données et la conformité dans chaque gabarit et chaque prisme dès la conception.",
    principles: [
      "Ne jamais ajouter la conformité en fin de parcours, la penser dès le début",
      "Gabarits PRIsME qui incluent systématiquement les volets NIST, ISO, Loi 25",
      "Tracer les décisions sensibles (sécurité, vie privée, accès) dans les prismes"
    ],
    color: "from-red-500 to-rose-600",
    reference: "Security / Privacy by Design, NIST CSF, ISO 27001, Loi 25",
  },
  {
    id: 110,
    title: "Mesure d’Adoption & de Valeur",
    icon: Activity,
    description: "Suivre l’utilisation réelle, la satisfaction et l’impact business des livrables et prismes PRIsME.",
    principles: [
      "Mesurer l’usage des prismes (consultations, mises à jour, commentaires)",
      "Relier les initiatives PRIsME à des indicateurs d’impact (ROI, risques, satisfaction)",
      "Ajuster la roadmap PRIsME en fonction de la valeur réellement perçue"
    ],
    color: "from-teal-500 to-emerald-500",
    reference: "Product Analytics (AARRR, North Star Metric), ITIL Continual Improvement",
  },
];

// Groupes par niveau (Stratégique, Stratégique-Tactique, Tactique-Opérationnel, Humain & Culture)
const strategicConcepts: Concept[] = [
  standardsBZ.find(c => c.id === 3)!, // Cadres Référentiels
  standardsBZ.find(c => c.id === 5)!, // IA Éthique & Responsable
  conceptsGeneraux.find(c => c.id === 13)!, // Alignement Business-TI
  standardsBZ.find(c => c.id === 1)!, // Panorama (Overview)
  prismeCoreConcepts[0], // Arbre
  prismeCoreConcepts[1], // Cascade
  prismeCoreConcepts[4], // PRIsME Master -> dérivés
].filter(Boolean);

const strategicTacticalConcepts: Concept[] = [
  standardsBZ.find(c => c.id === 2)!, // ASWAS → ASIS → Cible → GAP
  standardsBZ.find(c => c.id === 4)!, // KPI & Indicateurs de Performance
  standardsBZ.find(c => c.id === 6)!, // Magie de l'IA BZ
  conceptsBZ.find(c => c.id === 10)!,  // Roadmap Annuel
  prismeCoreConcepts[3], // Niveaux de maturité
  prismeCoreConcepts[10 - 101] ?? prismeCoreConcepts[6], // Mesure d’adoption & valeur (id 110) via index
].filter(Boolean);

const tacticalOperationalConcepts: Concept[] = [
  conceptsGeneraux.find(c => c.id === 12)!, // Sécurité par Design
  conceptsGeneraux.find(c => c.id === 16)!, // Excellence Opérationnelle
  conceptsBZ.find(c => c.id === 9)!,       // Spécifications d'exigences
  conceptsBZ.find(c => c.id === 8)!,       // Gouvernance RACI
  prismeCoreConcepts[2], // RACI & Gouvernance claire (doublon concept mais plus détaillé)
  prismeCoreConcepts[5], // Boucles d’apprentissage rapides
  prismeCoreConcepts[6], // Flow visuel & décisionnel
  prismeCoreConcepts[7], // Modularité & réutilisation
  prismeCoreConcepts[8], // Sécurité & conformité by design
].filter(Boolean);

const humanCultureConcepts: Concept[] = [
  conceptsBZ.find(c => c.id === 7)!,       // Profil humain
  conceptsGeneraux.find(c => c.id === 15)!, // Leadership Collaboratif
  conceptsGeneraux.find(c => c.id === 14)!, // Agilité Organisationnelle
  conceptsGeneraux.find(c => c.id === 11)!, // Innovation Continue
].filter(Boolean);

export default function Concepts() {
  const [expertVisionModal, setExpertVisionModal] = useState<{
    isOpen: boolean;
    expert: 'dany' | 'yannick' | 'francois' | null;
    item: any | null;
  }>({
    isOpen: false,
    expert: null,
    item: null,
  });

  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = lexDates['concepts_last_ai_analysis'] ?? null;

  return (
    <div className="min-h-screen">
      <Header 
        title="Concepts" 
        subtitle="Standards BZ Inc. d'affaires et principes fondamentaux pour l'excellence TI"
      />
      
      <div className="px-8 pb-12">
        {/* Date de dernière analyse AI - Discrète en haut */}
        {lastAnalysisDate && (
          <div className="flex items-center justify-end gap-2 mb-4 text-xs text-gray-500">
            <Clock className="w-3.5 h-3.5" />
            <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(lastAnalysisDate)}</span>
          </div>
        )}

        {/* Intro générale */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8 bg-gradient-to-r from-bz-blue to-blue-600 text-white rounded-xl p-8"
        >
          <h2 className="text-2xl font-bold mb-3">Philosophie PRIsME</h2>
          <p className="text-lg text-white/90">
            Ces standards et concepts BZ forment une seule base cohérente. 
            Ils alimentent PRIsME Master, les prismes employés et les prismes clients pour guider chaque décision.
          </p>
        </motion.div>

        {/* Liste fusionnée des concepts / standards, regroupés par niveau */}
        <div className="space-y-10">
          {/* Niveau Stratégique */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-bz-dark">Niveau Stratégique</h3>
            <p className="text-gray-700 text-sm mb-2">
              Concepts qui définissent la vision, les cadres de référence et l’architecture globale de PRIsME.
            </p>
            <div className="space-y-6">
              {strategicConcepts.map((concept, index) => {
                const Icon = concept.icon;
                return (
                  <motion.div
                    key={concept.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.04 }}
                    className="aikup-card overflow-hidden relative"
                  >
                    <div className={`bg-gradient-to-r ${concept.color} p-4 -m-6 mb-4 text-white`}>
                      <div className="flex items-center gap-3">
                        <Icon className="w-8 h-8" />
                        <h3 className="text-xl font-bold">{concept.title}</h3>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-4">{concept.description}</p>
                    <div>
                      <h4 className="font-semibold text-bz-dark mb-3">Principes clés:</h4>
                      <ul className="space-y-2">
                        {concept.principles.map((principle, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-2 text-sm text-gray-600"
                          >
                            <span className="text-bz-blue font-bold">→</span>
                            <span>{principle}</span>
                          </li>
                        ))}
                      </ul>
                      {concept.reference && (
                        <p className="mt-3 text-xs text-gray-500">
                          Référence : {concept.reference}
                        </p>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Niveau Stratégique–Tactique */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-bz-dark">Niveau Stratégique – Tactique</h3>
            <p className="text-gray-700 text-sm mb-2">
              Concepts qui traduisent la vision en objectifs, indicateurs et roadmaps concrètes.
            </p>
            <div className="space-y-6">
              {strategicTacticalConcepts.map((concept, index) => {
                const Icon = concept.icon;
                return (
                  <motion.div
                    key={concept.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.04 }}
                    className="aikup-card overflow-hidden relative"
                  >
                    <div className={`bg-gradient-to-r ${concept.color} p-4 -m-6 mb-4 text-white`}>
                      <div className="flex items-center gap-3">
                        <Icon className="w-8 h-8" />
                        <h3 className="text-xl font-bold">{concept.title}</h3>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-4">{concept.description}</p>
                    <div>
                      <h4 className="font-semibold text-bz-dark mb-3">Principes clés:</h4>
                      <ul className="space-y-2">
                        {concept.principles.map((principle, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-2 text-sm text-gray-600"
                          >
                            <span className="text-bz-blue font-bold">→</span>
                            <span>{principle}</span>
                          </li>
                        ))}
                      </ul>
                      {concept.reference && (
                        <p className="mt-3 text-xs text-gray-500">
                          Référence : {concept.reference}
                        </p>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Niveau Tactique–Opérationnel */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-bz-dark">Niveau Tactique – Opérationnel</h3>
            <p className="text-gray-700 text-sm mb-2">
              Concepts qui structurent l’exécution, la sécurité, les gabarits et les boucles d’amélioration.
            </p>
            <div className="space-y-6">
              {tacticalOperationalConcepts.map((concept, index) => {
                const Icon = concept.icon;
                return (
                  <motion.div
                    key={concept.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.04 }}
                    className="aikup-card overflow-hidden relative"
                  >
                    <div className={`bg-gradient-to-r ${concept.color} p-4 -m-6 mb-4 text-white`}>
                      <div className="flex items-center gap-3">
                        <Icon className="w-8 h-8" />
                        <h3 className="text-xl font-bold">{concept.title}</h3>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-4">{concept.description}</p>
                    <div>
                      <h4 className="font-semibold text-bz-dark mb-3">Principes clés:</h4>
                      <ul className="space-y-2">
                        {concept.principles.map((principle, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-2 text-sm text-gray-600"
                          >
                            <span className="text-bz-blue font-bold">→</span>
                            <span>{principle}</span>
                          </li>
                        ))}
                      </ul>
                      {concept.reference && (
                        <p className="mt-3 text-xs text-gray-500">
                          Référence : {concept.reference}
                        </p>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Niveau Humain & Culture */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-bz-dark">Niveau Humain & Culture</h3>
            <p className="text-gray-700 text-sm mb-2">
              Concepts qui touchent directement les personnes, le leadership, l’agilité et l’innovation au quotidien.
            </p>
            <div className="space-y-6">
              {humanCultureConcepts.map((concept, index) => {
                const Icon = concept.icon;
                return (
                  <motion.div
                    key={concept.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.04 }}
                    className="aikup-card overflow-hidden relative"
                  >
                    <div className={`bg-gradient-to-r ${concept.color} p-4 -m-6 mb-4 text-white`}>
                      <div className="flex items-center gap-3">
                        <Icon className="w-8 h-8" />
                        <h3 className="text-xl font-bold">{concept.title}</h3>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-4">{concept.description}</p>
                    <div>
                      <h4 className="font-semibold text-bz-dark mb-3">Principes clés:</h4>
                      <ul className="space-y-2">
                        {concept.principles.map((principle, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-2 text-sm text-gray-600"
                          >
                            <span className="text-bz-blue font-bold">→</span>
                            <span>{principle}</span>
                          </li>
                        ))}
                      </ul>
                      {concept.reference && (
                        <p className="mt-3 text-xs text-gray-500">
                          Référence : {concept.reference}
                        </p>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <ExpertVisionModal
        isOpen={expertVisionModal.isOpen}
        onClose={() => setExpertVisionModal({ isOpen: false, expert: null, item: null })}
        expert={expertVisionModal.expert}
        item={expertVisionModal.item}
      />
    </div>
  );
}
